import React, { ChangeEventHandler } from "react";
import { isNullOrWhiteSpace } from "../../shared/string";

export type RadioButtonsVariant = "normal" | "inverted" | "inverted-vertical";

interface GetInputClassNameProps {
  checked: boolean;
  error: boolean;
  readonly: boolean;
  variant: RadioButtonsVariant;
}

const getInputClassName = ({
  checked,
  error,
  readonly,
  variant
}: GetInputClassNameProps) => {
  if (variant === "normal") {
    return "w-6 h-6 mr-2";
  } else {
    let classes =
      "shrink-0 w-8 h-8 mr-2 rounded-full outline-none bg-transparent transition-all duration-100 appearance-none";

    if (checked) {
      classes += " border-7";
    } else {
      classes += " border";
    }

    if (error) {
      classes += " border-brand-tertiary-50 shadow-outline-error";
    } else {
      if (checked && !readonly) {
        classes += " border-brand-primary-70";
      } else {
        classes += " border-brand-primary-dim-80";
      }

      if (!readonly) {
        classes += " hover:border-brand-primary-70";
      }

      classes += " focus:border-brand-primary-70 focus:shadow-outline";
    }

    return classes;
  }
};

interface ControlledRadioButtonsProps {
  additionalClassName?: string;
  applyBackground?: boolean;
  error?: boolean;
  label?: string;
  largeLabel?: boolean;
  name: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  options: {
    label: string;
    value: string | number;
  }[];
  readonly?: boolean;
  value: string | undefined;
  variant?: RadioButtonsVariant;
}

export const ControlledRadioButtons = ({
  additionalClassName = "",
  applyBackground = false,
  error,
  label,
  largeLabel = false,
  name,
  onChange,
  options,
  readonly,
  value,
  variant = "normal"
}: ControlledRadioButtonsProps) => {
  const labelClass = "inline-flex items-center text-lg text-neutral-40";
  let optionsWrapperClass: string;

  if (variant === "normal") {
    optionsWrapperClass = "inline-flex flex-row";

    if (applyBackground) {
      optionsWrapperClass +=
        " px-8 py-6 space-x-6 bg-brand-primary-100 border border-brand-primary-95 rounded-lg";
    }
  } else if (variant === "inverted-vertical") {
    optionsWrapperClass = "flex flex-col max-w-full items-start gap-4";
  } else {
    optionsWrapperClass = "flex flex-row flex-wrap max-w-full items-start gap-6";
  }

  return (
    <div className={`inline-flex flex-col ${additionalClassName}`}>
      {!isNullOrWhiteSpace(label) && (
        <label
          htmlFor={name}
          className={
            largeLabel
              ? "mb-4 text-brand-primary-20 text-lg lg:text-2xl"
              : "mb-2 text-neutral-40 font-semibold"
          }
        >
          {label}
        </label>
      )}
      <div className={optionsWrapperClass}>
        {readonly && <input value={value} id={name} name={name} type="hidden" />}
        {options?.map((option, index) => {
          const checked = value === option.value;

          return (
            <label key={index} className={labelClass}>
              <input
                checked={checked}
                className={getInputClassName({
                  checked,
                  error: error ?? false,
                  readonly: readonly ?? false,
                  variant
                })}
                name={name}
                onChange={onChange}
                disabled={readonly}
                type="radio"
                value={option.value}
              />{" "}
              {option.label}
            </label>
          );
        })}
      </div>
    </div>
  );
};

